import i18n from 'i18next'

export const fetchResultsEnrichment = {
  orderFacets (resp) {
    const { searcher } = resp.config

    const facetsOrder = {
      education: ['education_type', 'education_level', 'subject_area', 'subject', 'period', 'pace_of_study',
                  'education_form', 'education_language', 'time_of_study', 'education_organization'],
    }[searcher] || [] // depending on searcher (both niche and global)

    resp?.data?.facets?.sort((a, b) => facetsOrder.indexOf(a.id) - facetsOrder.indexOf(b.id))

    return resp
  },
  orderFacetsItems (resp) {
    const { searcher } = resp.config

    // NOTE: frontend is only handling facet values sorting were we need any other order than alphabetic
    const facetsItemOrder = {
      education: {
        pace_of_study: ['Up to 25%', 'Up to 50%', 'Up to 75%', 'Full-time',
                        'Upp till 25%', 'Upp till 50%', 'Upp till 75%', 'Helfart',],
        time_of_study: ['Day', 'Afternoon', 'Evening', 'Weekends', 'Mixed', 'No teaching',
                        'Dag', 'Eftermiddag', 'Kväll', 'Veckoslut', 'Blandad', 'Ingen undervisningstid'],
      }
    }[searcher] || {} // depending on searcher (both niche and global)

    Object.entries(facetsItemOrder).forEach(([facetId, facetOrder]) => {
      const index = resp?.data?.facets?.findIndex(f => f.id === facetId)
      resp?.data?.facets[index]?.filters
        .sort((a, b) => (facetOrder.indexOf(a.displayName) - facetOrder.indexOf(b.displayName)))
    })

    return resp
  },
  orderFacetsItemsEducationPeriod (resp) {
    const { searcher } = resp.config

    if (searcher === 'education') {
      const index = resp?.data?.facets?.findIndex(f => f.id === 'period')
      resp?.data?.facets[index]?.filters
        .sort((a, b) => {
          const regex = /^([^ ]+)?(?:\s)?(\d{4})$/
          const [, aPeriod, aYear] = a.displayName.match(regex)
          const [, bPeriod, bYear] = b.displayName.match(regex)
          const periods = ['Vår', 'Sommar', 'Höst', 'Spring', 'Summer', 'Autumn']

          if (aYear === bYear)
            return periods.indexOf(aPeriod) - periods.indexOf(bPeriod)
          return Number(aYear) - Number(bYear)
        })
    }

    return resp
  },
  addFacetsDataStructure (resp) {
    const { searcher } = resp.config

    const facetsDataArray = {
      education: {
        open_for_application: ['MAIN', 'CHECKBOX', false, false, false],
        subject: [null, null, true, 'subject_area', false],
        education_department: [null, null, true, 'education_faculty', true],
      }
    }[searcher] || [] // depending on searcher (both niche and global)

    Object.entries(facetsDataArray).forEach(([facetId, dataArray]) => {
      const index = resp?.data?.facets?.findIndex(f => f.id === facetId)
      const facet = resp?.data?.facets[index]
      facet.area = dataArray[0]
      facet.inputType = dataArray[1]
      facet.disabled = dataArray[2]
      facet.enableOnParam = dataArray[3]
      facet.hideNonSelectable = dataArray[4]
    })

    return resp
  },
  addDocumentBreadcrumb (resp) {
    const t = i18n.t.bind(i18n)

    resp?.data?.documentList?.documents.forEach(doc => {
      const tokens = doc.url?.split('/')?.slice(1) || []
      if (tokens.length !== 1) tokens.splice(-1, 1)
      if (!tokens.length || doc.url === '/') return

      doc.__breadcrumb__ = [{
        displayName: t('Home'), url: '/' },
        ...tokens.map(displayName => ({
          displayName: displayName.replace(/-/g, ' ')
        }))
      ]
    })
    return resp
  },
  addDocumentResearchTypeShort (resp) {
    resp?.data?.documentList?.documents.forEach(doc => {
      if (doc.type !== 'research_project' && doc.type !== 'research_story') return

      doc.__research_type_short__ = doc.research_type
        ?.replace(/^(Forsknings|Research\s)/, '')
        ?.replace(/^(.)/, (c) => c.toUpperCase())
    })

    return resp
  },

  /**
   * We have an unsorted object (orgnames) with the structure { "ladokid": "Translated string"}
   * and we have an array with objects from i3 with the structure as follows [{ displayName: "ladokId"}].
   * To properly sort the structure of the i3 array we have to create a sorted array of
   * ladokIds sorted by their translated names and then use that array to sort the i3 array of
   * objects based on the index of the first array.
   */
  sortOrganizations (resp) {
    const orgNames = resp?.data?.extraData?.orgNames

    if (typeof orgNames === typeof undefined) { return resp }

    const alphaNumericSort = (a, b) => {
      if (a.displayName < b.displayName) { return -1 }
      if (a.displayName > b.displayName) { return 1 }
      return 0
    }

    const sortedOrgnames = Object.keys(orgNames).map((key) => ({
        displayName: orgNames[key],
        ladokid: key
      })).sort(alphaNumericSort).map(a => a.ladokid).flat()

    resp?.data?.facets[0].filters.sort(alphaNumericSort).map(facet => facet.children.sort((a, b) => sortedOrgnames.indexOf(a.displayName) - sortedOrgnames.indexOf(b.displayName)));

    return resp
  },

  normalizeHeaders (resp) {

    const documents = resp?.data?.documentList?.documents
    const orgNames = resp?.data?.extraData?.orgNames

    if (typeof documents === typeof undefined) { return resp }
    if (typeof orgNames === typeof undefined) { return resp }

    const normalizedDocuments = documents.map(doc => {

      doc.organization = orgNames[doc.organization_code]

      return doc
    })

    resp.data.documentList.documents = normalizedDocuments

    return resp
  },

  replaceOrganizationNames(resp) {
    const extraData = resp?.data?.extraData

    if (extraData?.orgNames) {
      const facets = resp?.data?.facets[0]
      const organizationsF = facets?.filters[0]?.children
      const organizationsI = facets?.filters[1]?.children

      const replaceDisplayNameWithOrgName = (org) => {
        org.id = org.displayName
        const orgName = extraData.orgNames[org.displayName]
        if (orgName) {
          org.displayName = orgName
        }
      }

      if (organizationsF) {
        organizationsF.forEach(replaceDisplayNameWithOrgName)
      }

      if (organizationsI) {
        organizationsI.forEach(replaceDisplayNameWithOrgName)
      }
    }

    return resp
  },

  removeUnmappedOrganizations(resp) {
    const extraData = resp?.data?.extraData

    if (extraData && extraData.orgNames) {
      resp.data.facets.forEach(facet => {
        facet.filters.forEach(filter => {
          if (filter.children) {
            filter.children = filter.children.filter(child => extraData.orgNames.hasOwnProperty(child.displayName))
          }
        })
      })
    }

    return resp
  },

  removeInvalidParams(resp) {
    const extraData = resp?.data?.extraData

    if (extraData) {
      const allowedTypes = ['I', 'F']
      resp?.data?.facets.forEach(facet => {
        facet.filters = facet.filters.filter(filter => allowedTypes.includes(filter.displayName))
      })
    }

    return resp
  }
}
